import api from './api';

export const DEFAULT_TYPE_CODES = [];
export const DEFAULT_REGISTRATION_PREFIXES = [];

// These ICAO addresses are right; the feed's registration is not.
const HEX_BUNO_OVERRIDES = {
  'ADFF03': '165958',
  'AE08B3': '165960',
  'AE08B5': '165961',
};

const applyHexBunoOverride = (aircraft) => {
  if (!aircraft || !aircraft.hex_id) return aircraft;
  const buno = HEX_BUNO_OVERRIDES[String(aircraft.hex_id).trim().toUpperCase()];
  if (!buno) return aircraft;
  return {
    ...aircraft,
    buno,
    registration: buno,
    callsign: buno,
  };
};

let cachedFilters = null;

export const extractAdsbExchangeAircraft = (data) => {
  if (Array.isArray(data)) return data;
  if (data?.ac && Array.isArray(data.ac)) return data.ac;
  if (data?.aircraft && Array.isArray(data.aircraft)) return data.aircraft;
  if (data?.data && Array.isArray(data.data)) return data.data;
  if (data && typeof data === 'object') {
    return Object.values(data).filter(item =>
      item && typeof item === 'object' && (item.hex || item.icao || item.hex_id)
    );
  }
  return [];
};

export const transformAdsbExchangeAircraft = (aircraft, options = {}) => {
  const { convertAltitudeToMeters = false } = options;
  const hex = aircraft.hex || aircraft.icao || aircraft.hex_id || '';
  const registration = aircraft.r || aircraft.call || aircraft.callsign || '';

  let lat = aircraft.lat || aircraft.latitude || 0;
  let lon = aircraft.lon || aircraft.longitude || 0;
  let alt = null;
  let gs = aircraft.gs || aircraft.ground_speed || 0;
  let track = aircraft.track || aircraft.heading || 0;
  let vr = aircraft.vr || aircraft.vertical_rate || 0;

  if (aircraft.alt !== null && aircraft.alt !== undefined) {
    alt = aircraft.alt;
  } else if (aircraft.altitude !== null && aircraft.altitude !== undefined) {
    alt = aircraft.altitude;
  } else if (aircraft.geom_alt !== null && aircraft.geom_alt !== undefined) {
    alt = aircraft.geom_alt;
  } else if (aircraft.alt_geom !== null && aircraft.alt_geom !== undefined) {
    alt = aircraft.alt_geom;
  } else if (aircraft.baro_alt !== null && aircraft.baro_alt !== undefined) {
    alt = aircraft.baro_alt;
  }

  if (aircraft.trace && Array.isArray(aircraft.trace) && aircraft.trace.length > 0) {
    const latestTrace = aircraft.trace[aircraft.trace.length - 1];
    if (Array.isArray(latestTrace) && latestTrace.length >= 4) {
      if (latestTrace[1] !== null && latestTrace[1] !== undefined) lat = latestTrace[1];
      if (latestTrace[2] !== null && latestTrace[2] !== undefined) lon = latestTrace[2];

      if (latestTrace.length > 8 && latestTrace[8] && typeof latestTrace[8] === 'object') {
        const traceData = latestTrace[8];
        if (traceData.alt_geom !== null && traceData.alt_geom !== undefined && typeof traceData.alt_geom === 'number') {
          alt = traceData.alt_geom;
        } else if (traceData.alt !== null && traceData.alt !== undefined && typeof traceData.alt === 'number') {
          alt = traceData.alt;
        }
        if (traceData.track !== undefined) track = traceData.track;
        if (traceData.baro_rate !== undefined) vr = traceData.baro_rate;
      }

      if (alt === null && latestTrace.length >= 11 && latestTrace[10] !== null && latestTrace[10] !== undefined && typeof latestTrace[10] === 'number') {
        alt = latestTrace[10];
      }

      if (alt === null && latestTrace[3] !== null && latestTrace[3] !== undefined) {
        if (latestTrace[3] === 'ground') {
          alt = 0;
        } else if (typeof latestTrace[3] === 'number') {
          alt = latestTrace[3];
        }
      }

      if (latestTrace.length >= 5 && latestTrace[4] !== null && latestTrace[4] !== undefined) gs = latestTrace[4];
      if (latestTrace.length >= 6 && latestTrace[5] !== null && latestTrace[5] !== undefined) track = latestTrace[5];
      if (latestTrace.length >= 8 && latestTrace[7] !== null && latestTrace[7] !== undefined) vr = latestTrace[7];
    }
  }

  let altMeters = 0;
  if (alt !== null && typeof alt === 'number' && !isNaN(alt)) {
    altMeters = convertAltitudeToMeters ? alt * 0.3048 : alt;
  } else if (alt === 0) {
    altMeters = 0;
  }

  let isOnGround;
  if (aircraft.is_on_ground !== null && aircraft.is_on_ground !== undefined) {
    isOnGround = Boolean(aircraft.is_on_ground);
  } else {
    const groundThreshold = convertAltitudeToMeters ? 30.48 : 100;
    isOnGround = altMeters < groundThreshold;
  }

  return applyHexBunoOverride({
    hex_id: hex,
    callsign: registration,
    registration,
    buno: aircraft.buno || registration || null,
    latitude: lat,
    longitude: lon,
    altitude: altMeters,
    ground_speed: gs,
    track,
    vertical_rate: vr,
    squawk: aircraft.squawk || '',
    is_on_ground: isOnGround,
    timestamp: aircraft.timestamp || aircraft.time || new Date().toISOString(),
    site: aircraft.site || null,
    source: 'adsbexchange',
  });
};

export const fetchAdsbFilters = async () => {
  if (cachedFilters) return cachedFilters;
  try {
    const response = await api.get('/adsb-configuration/filters/');
    cachedFilters = response.data;
    return cachedFilters;
  } catch (error) {
    console.warn('Failed to load ADSB filter configuration:', error);
    return {
      rapidapi_enabled: false,
      has_rapidapi_key: false,
      aircraft_type_codes: DEFAULT_TYPE_CODES,
      registration_prefixes: DEFAULT_REGISTRATION_PREFIXES,
      adsb_exchange_poll_interval_ms: 2000,
    };
  }
};

export const clearAdsbFiltersCache = () => {
  cachedFilters = null;
};

export const fetchAdsbExchangeAircraft = async (options = {}) => {
  const { convertAltitudeToMeters = false } = options;
  const filters = await fetchAdsbFilters();

  if (!filters.rapidapi_enabled || !filters.has_rapidapi_key) {
    return [];
  }

  try {
    const response = await api.get('/adsb-exchange/');
    return extractAdsbExchangeAircraft(response.data)
      .map(aircraft => transformAdsbExchangeAircraft(aircraft, { convertAltitudeToMeters }))
      .filter(aircraft => aircraft.hex_id && aircraft.latitude && aircraft.longitude);
  } catch (error) {
    console.warn('ADSB Exchange proxy error:', error);
    return [];
  }
};

export const fetchBackendAdsbAircraft = async () => {
  const response = await api.get('/adsb-aircraft/');
  const aircraftData = response.data?.results || response.data;
  if (!Array.isArray(aircraftData)) return [];
  return aircraftData.map(applyHexBunoOverride);
};

export const fetchMergedAdsbAircraft = async (options = {}) => {
  const [backendResult, exchangeResult] = await Promise.allSettled([
    fetchBackendAdsbAircraft(),
    fetchAdsbExchangeAircraft(options),
  ]);

  const aircraftByHex = new Map();
  if (backendResult.status === 'fulfilled') {
    backendResult.value.forEach(aircraft => {
      if (aircraft.hex_id) {
        aircraft.source = 'backend';
        aircraftByHex.set(aircraft.hex_id, aircraft);
      }
    });
  }
  if (exchangeResult.status === 'fulfilled') {
    exchangeResult.value.forEach(aircraft => {
      if (aircraft.hex_id && !aircraftByHex.has(aircraft.hex_id)) {
        aircraftByHex.set(aircraft.hex_id, aircraft);
      }
    });
  }

  return Array.from(aircraftByHex.values());
};
